import jsPDF from 'jspdf';
import { TylerState } from '../utils/TylerState';

interface YearlyData {
  openingBalance?: number;
  gas?: number;
  investmentReturn?: number;
  closingBalance?: number;
  monthlyCosts?: number;
  sellCar?: number;
}

export class CarPDFService {
  // Professional financial statement formatting constants
  private readonly MARGIN_INCH = 25.4; // 1 inch in mm
  private readonly PAGE_WIDTH = 210; // A4 width in mm
  private readonly PAGE_HEIGHT = 297; // A4 height in mm

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  private formatCurrencyDetailed(amount: number): string {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatPercent(amount: number): string {
    return `${amount.toFixed(2)}%`;
  }

  // Professional header with company branding
  private addHeader(pdf: jsPDF, scenarioName?: string): number {
    const margin = this.MARGIN_INCH;
    const headerHeight = 20;

    // Red background header bar
    pdf.setFillColor(220, 38, 38); // #dc2626 - website red
    pdf.rect(0, 0, this.PAGE_WIDTH, headerHeight, 'F');

    // Company name - white on red
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('times', 'bold');
    pdf.text('Larry\'s Help', margin, 12);

    // Website URL - small, right aligned
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.text('LarrysHelp.org', this.PAGE_WIDTH - margin, 12, { align: 'right' });

    // Reset to black text
    pdf.setTextColor(0, 0, 0);

    // Scenario name as title (if provided)
    let yPos = headerHeight + 12;
    if (scenarioName) {
      pdf.setFontSize(16);
      pdf.setFont('times', 'bold');
      pdf.text(scenarioName, margin, yPos);
      yPos += 8;
    }

    // Subtitle
    pdf.setFontSize(11);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(60, 60, 60); // Dark gray
    pdf.text('Vehicle Ownership Cost Analysis', margin, yPos);

    // Thin underline
    pdf.setDrawColor(220, 38, 38); // Match header red
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPos + 2, this.PAGE_WIDTH - margin, yPos + 2);

    pdf.setTextColor(0, 0, 0); // Reset to black
    return yPos + 10; // Return starting Y position after header
  }

  // Professional footer with page numbers and prepared by
  private addFooter(pdf: jsPDF, pageNum: number, totalPages: number, reportDate: string): void {
    const margin = this.MARGIN_INCH;

    // Bottom border line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, this.PAGE_HEIGHT - margin + 5, this.PAGE_WIDTH - margin, this.PAGE_HEIGHT - margin + 5);

    // Prepared by (left)
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Prepared by Larry\'s Help', margin, this.PAGE_HEIGHT - margin + 10);

    // Date (center)
    pdf.text(reportDate, this.PAGE_WIDTH / 2, this.PAGE_HEIGHT - margin + 10, { align: 'center' });

    // Page number (right)
    pdf.text(`Page ${pageNum} of ${totalPages}`, this.PAGE_WIDTH - margin, this.PAGE_HEIGHT - margin + 10, { align: 'right' });
  }

  // Professional section heading with underline
  private addSectionTitle(pdf: jsPDF, title: string, y: number): number {
    const margin = this.MARGIN_INCH;

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('times', 'bold');
    pdf.text(title, margin, y);

    // Underline the section title
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    const textWidth = pdf.getTextWidth(title);
    pdf.line(margin, y + 1, margin + textWidth, y + 1);

    pdf.setFont('times', 'normal');
    return y + 8;
  }

  // Add a table with professional financial statement styling
  private addTable(
    pdf: jsPDF,
    headers: string[],
    rows: (string | number)[][],
    yStart: number,
    columnWidths: number[]
  ): number {
    const margin = this.MARGIN_INCH;
    let y = yStart;

    // Header row with underline
    pdf.setFont('times', 'bold');
    pdf.setFontSize(10);
    let x = margin;
    headers.forEach((header, i) => {
      if (i === 0) {
        pdf.text(header, x, y); // Left align first column
      } else {
        pdf.text(header, x + columnWidths[i] - 5, y, { align: 'right' }); // Right align numbers
      }
      x += columnWidths[i];
    });

    // Underline header
    pdf.setLineWidth(0.3);
    pdf.line(margin, y + 1, margin + columnWidths.reduce((a, b) => a + b, 0), y + 1);
    y += 6;

    // Data rows
    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    rows.forEach((row, rowIndex) => {
      x = margin;
      row.forEach((cell, i) => {
        const cellText = typeof cell === 'number' ? this.formatCurrency(cell) : cell;
        if (i === 0) {
          pdf.text(cellText, x, y); // Left align first column
        } else {
          pdf.text(cellText, x + columnWidths[i] - 5, y, { align: 'right' }); // Right align numbers
        }
        x += columnWidths[i];
      });
      y += 5;

      // Add line after last row (total)
      if (rowIndex === rows.length - 1) {
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, margin + columnWidths.reduce((a, b) => a + b, 0), y);
        y += 2;
      }
    });

    return y + 5;
  }

  // Main PDF generation function - reads from TylerState
  // eslint-disable-next-line max-lines-per-function, max-statements
  async generateProfessionalPDF(
    _unusedData: any, // Keep for API compatibility but don't use
    saveName: string
  ): Promise<void> {
    try {
      // Read all data from TylerState instead of props
      const carAInputs = TylerState.car.carA.inputs;
      const carAOutputs = TylerState.car.carA.outputs;
      const includeFinance = TylerState.car.includeFinanceScenario;
      const includeLease = TylerState.car.includeLeaseScenario;

      if (!carAInputs || !carAOutputs) {
        console.error('Car data not found in TylerState');
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = this.MARGIN_INCH;
      const contentWidth = this.PAGE_WIDTH - (2 * margin);
      const reportDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Calculate total pages based on included scenarios
      let totalPages = 2; // Cover + Buy scenario always included
      if (includeFinance) totalPages++;
      if (includeLease) totalPages++;

      let currentPage = 1;

      // ===========================================
      // PAGE 1: SUMMARY
      // ===========================================
      let yPosition = this.addHeader(pdf, saveName || carAInputs.scenarioName || 'Vehicle Analysis');
      yPosition += 5;

      // Scenario name
      pdf.setFontSize(12);
      pdf.setFont('times', 'bold');
      pdf.text('Vehicle:', margin, yPosition);
      pdf.setFont('times', 'normal');
      pdf.text(saveName || carAInputs.scenarioName || 'Car Analysis', margin + 30, yPosition);
      yPosition += 8;

      // Analysis period
      pdf.setFont('times', 'bold');
      pdf.text('Analysis Period:', margin, yPosition);
      pdf.setFont('times', 'normal');
      pdf.text(`${carAInputs.ownershipTimeframe} years`, margin + 30, yPosition);
      yPosition += 8;

      pdf.setFont('times', 'bold');
      pdf.text('Total Distance:', margin, yPosition);
      pdf.setFont('times', 'normal');
      pdf.text(`${(carAInputs.annualMileage * carAInputs.ownershipTimeframe).toLocaleString()} ${carAInputs.region === 'Canada' ? 'km' : 'miles'}`, margin + 30, yPosition);
      yPosition += 15;

      // Summary table - professional financial statement style
      yPosition = this.addSectionTitle(pdf, 'COST COMPARISON SUMMARY', yPosition);
      yPosition += 3;

      const summaryRows: (string | number)[][] = [
        ['Purchase Scenario', carAOutputs.buy.totalCost, carAOutputs.buy.costPerMonth, this.formatCurrencyDetailed(carAOutputs.buy.costPerKm)]
      ];

      if (includeFinance) {
        summaryRows.push(['Financing Scenario', carAOutputs.finance.totalCost, carAOutputs.finance.costPerMonth, this.formatCurrencyDetailed(carAOutputs.finance.costPerKm)]);
      }

      if (includeLease) {
        summaryRows.push(['Lease Scenario', carAOutputs.lease.totalCost, carAOutputs.lease.costPerMonth, this.formatCurrencyDetailed(carAOutputs.lease.costPerKm)]);
      }

      yPosition = this.addTable(
        pdf,
        ['Scenario', 'Total Cost', 'Cost/Month', 'Cost/km'],
        summaryRows,
        yPosition,
        [53, 35, 35, 36]
      );

      yPosition += 5;

      // Resale Value Analysis
      yPosition = this.addSectionTitle(pdf, 'RESALE VALUE ANALYSIS', yPosition);
      yPosition += 3;

      const expectedResaleValue = carAInputs.carPrice * (carAInputs.resaleValue / 100);
      const costsToSell = expectedResaleValue * 0.05; // Assume 5% selling costs
      const netProceeds = expectedResaleValue - costsToSell;

      const resaleRows: (string | number)[][] = [
        ['Expected resale value', expectedResaleValue],
        ['Less: Costs to sell (5%)', -costsToSell],
        ['Net proceeds from sale', netProceeds]
      ];

      yPosition = this.addTable(
        pdf,
        ['Item', 'Amount'],
        resaleRows,
        yPosition,
        [106, 53]
      );

      yPosition += 5;

      // Recommendation
      yPosition = this.addSectionTitle(pdf, 'RECOMMENDATION', yPosition);
      yPosition += 3;

      const lowestCost = Math.min(
        carAOutputs.buy.totalCost,
        includeFinance ? carAOutputs.finance.totalCost : Infinity,
        includeLease ? carAOutputs.lease.totalCost : Infinity
      );

      let recommendedScenario = 'Purchase';
      if (includeFinance && carAOutputs.finance.totalCost === lowestCost) {
        recommendedScenario = 'Financing';
      } else if (includeLease && carAOutputs.lease.totalCost === lowestCost) {
        recommendedScenario = 'Lease';
      }

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      const recommendText = `Based on the ${carAInputs.ownershipTimeframe}-year analysis period, the ${recommendedScenario} scenario offers the lowest total cost of ownership at ${this.formatCurrency(lowestCost)}.`;
      const lines = pdf.splitTextToSize(recommendText, contentWidth);
      lines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });

      // Notes section - comprehensive input listing
      yPosition += 5;
      yPosition = this.addSectionTitle(pdf, 'NOTES AND ASSUMPTIONS', yPosition);
      yPosition += 3;

      pdf.setFontSize(9);
      const notes = [
        `Vehicle price: ${this.formatCurrency(carAInputs.totalCost)}`,
        `Sales tax: ${carAInputs.useTaxesPercent ? carAInputs.taxes + '%' : this.formatCurrency(carAInputs.taxes)}`,
        `Fees and charges: ${this.formatCurrency(carAInputs.fees)}`,
        `Ownership period: ${carAInputs.ownershipTimeframe} years`,
        `Annual mileage: ${carAInputs.annualMileage.toLocaleString()} ${carAInputs.region === 'Canada' ? 'km' : 'miles'}`,
        `Fuel efficiency: ${carAInputs.fuelEfficiency} ${carAInputs.region === 'Canada' ? 'L/100km' : 'MPG'}`,
        `Fuel cost per ${carAInputs.region === 'Canada' ? 'liter' : 'gallon'}: ${this.formatCurrency(carAInputs.fuelCostPerUnit)}`,
        `Annual insurance: ${this.formatCurrency(carAInputs.annualInsurance)}`,
        `Annual maintenance: ${this.formatCurrency(carAInputs.maintenanceCost)}`,
        `Other annual costs: ${this.formatCurrency(carAInputs.otherCarCosts || 0)}`,
        `Resale value: ${carAInputs.useDepreciationPercent ? carAInputs.depreciationPercent + '% depreciation' : this.formatCurrency(carAInputs.expectedValueAtEnd)}`,
        `Selling costs: ${carAInputs.useSellingCostsPercent ? carAInputs.sellingCostsPercent + '% of resale' : this.formatCurrency(carAInputs.sellingCosts)}`,
        `Investment return rate: ${carAInputs.investmentReturnRate}% annually`,
        `Inflation rate: ${carAInputs.inflationRate || 0}% annually`
      ];

      // Add financing details if included
      if (includeFinance) {
        notes.push(`Down payment: ${this.formatCurrency(carAInputs.downPayment)}`);
        notes.push(`Interest rate: ${carAInputs.interestRate}%`);
        notes.push(`Financing term: ${carAInputs.financingYears} years`);
      }

      // Add leasing details if included
      if (includeLease) {
        notes.push(`Lease down payment: ${this.formatCurrency(carAInputs.leaseDownPayment)}`);
        notes.push(`Lease term: ${carAInputs.leaseTerm} years`);
        notes.push(`Lease payment: ${this.formatCurrency(carAInputs.leasePayment)}/month`);
        notes.push(`Lease mileage allowance: ${carAInputs.totalMileageAllowance.toLocaleString()} ${carAInputs.region === 'Canada' ? 'km' : 'miles'}`);
      }

      notes.push('All costs include opportunity cost of capital');

      notes.forEach(note => {
        // Check if we need a new page
        if (yPosition > this.PAGE_HEIGHT - 50) {
          this.addFooter(pdf, currentPage, totalPages, reportDate);
          pdf.addPage();
          currentPage++;
          totalPages++; // Adjust total pages if we added one
          yPosition = this.addHeader(pdf, saveName || carAInputs.scenarioName || 'Vehicle Analysis');
          yPosition = this.addSectionTitle(pdf, 'NOTES AND ASSUMPTIONS (continued)', yPosition);
          yPosition += 3;
        }

        pdf.text(`• ${note}`, margin, yPosition);
        yPosition += 5;
      });

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 2: BUY SCENARIO DETAIL
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, saveName || carAInputs.scenarioName || 'Vehicle Analysis');

      yPosition = this.addSectionTitle(pdf, 'PURCHASE SCENARIO - DETAILED ANALYSIS', yPosition);
      yPosition += 5;

      // Initial Investment
      yPosition = this.addSectionTitle(pdf, 'Initial Investment', yPosition);
      yPosition += 3;

      const taxAmount = carAInputs.useTaxesPercent
        ? carAInputs.totalCost * (carAInputs.taxes / 100)
        : carAInputs.taxes;

      const buyInitialRows: (string | number)[][] = [
        ['Vehicle price', carAInputs.totalCost],
        ['Sales tax' + (carAInputs.useTaxesPercent ? ` (${carAInputs.taxes}%)` : ''), taxAmount],
        ['Fees and charges', carAInputs.fees],
        ['Total initial investment', carAInputs.totalCost + taxAmount + carAInputs.fees]
      ];

      yPosition = this.addTable(pdf, ['Description', 'Amount'], buyInitialRows, yPosition, [106, 53]);

      // Annual Costs
      yPosition = this.addSectionTitle(pdf, 'Annual Operating Costs', yPosition);
      yPosition += 3;

      const fuelCostPerYear = carAInputs.region === 'Canada'
        ? (carAInputs.annualMileage / 100) * carAInputs.fuelEfficiency * carAInputs.fuelCostPerUnit
        : (carAInputs.annualMileage / carAInputs.fuelEfficiency) * carAInputs.fuelCostPerUnit;

      const buyAnnualRows: (string | number)[][] = [
        ['Fuel costs', fuelCostPerYear],
        ['Insurance', carAInputs.annualInsurance],
        ['Maintenance', carAInputs.maintenanceCost],
        ['Other costs', carAInputs.otherCarCosts],
        ['Total annual costs', fuelCostPerYear + carAInputs.annualInsurance + carAInputs.maintenanceCost + carAInputs.otherCarCosts]
      ];

      yPosition = this.addTable(pdf, ['Description', 'Amount'], buyAnnualRows, yPosition, [106, 53]);

      // Year-by-year cash flows with detailed cost breakdown
      if (carAOutputs.buy.yearlyData && carAOutputs.buy.yearlyData.length > 0) {
        yPosition = this.addSectionTitle(pdf, 'Year-by-Year Cash Flows', yPosition);
        yPosition += 3;

        const yearlyRows: (string | number)[][] = [];
        carAOutputs.buy.yearlyData.forEach((yearData: any, index: number) => {
          yearlyRows.push([
            `Year ${index + 1}`,
            yearData.openingBalance || 0,
            yearData.cashPurchase || 0,
            yearData.gas || 0,
            (yearData.insurance || 0) + (yearData.maintenance || 0) + (yearData.otherCosts || 0),
            yearData.investmentReturn || 0,
            yearData.sellCar || 0,
            yearData.closingBalance || 0
          ]);
        });

        yPosition = this.addTable(
          pdf,
          ['Year', 'Opening', 'Purchase', 'Fuel', 'Other', 'Opp. Cost', 'Resale', 'Closing'],
          yearlyRows,
          yPosition,
          [14, 22, 22, 19, 19, 21, 21, 21]
        );
      }

      // Total Cost Summary
      yPosition += 5;
      yPosition = this.addSectionTitle(pdf, 'Total Cost Summary', yPosition);
      yPosition += 3;

      const buySummaryRows: (string | number)[][] = [
        ['Total cost of ownership', carAOutputs.buy.totalCost],
        ['Cost per month', carAOutputs.buy.costPerMonth],
        [`Cost per ${carAInputs.region === 'Canada' ? 'kilometer' : 'mile'}`, this.formatCurrencyDetailed(carAOutputs.buy.costPerKm)]
      ];

      yPosition = this.addTable(pdf, ['Metric', 'Amount'], buySummaryRows, yPosition, [106, 53]);

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 3: FINANCE SCENARIO (if included)
      // ===========================================
      if (includeFinance) {
        pdf.addPage();
        currentPage++;
        yPosition = this.addHeader(pdf, saveName || carAInputs.scenarioName || 'Vehicle Analysis');

        yPosition = this.addSectionTitle(pdf, 'FINANCING SCENARIO - DETAILED ANALYSIS', yPosition);
        yPosition += 5;

        // Loan Details
        yPosition = this.addSectionTitle(pdf, 'Loan Details', yPosition);
        yPosition += 3;

        const financeInitialRows: (string | number)[][] = [
          ['Vehicle price', carAInputs.totalCost],
          ['Sales tax', carAInputs.taxes],
          ['Fees and charges', carAInputs.fees],
          ['Down payment', -carAInputs.downPayment],
          ['Amount financed', carAInputs.totalCost + carAInputs.taxes + carAInputs.fees - carAInputs.downPayment]
        ];

        yPosition = this.addTable(pdf, ['Description', 'Amount'], financeInitialRows, yPosition, [106, 53]);

        // Loan Terms
        yPosition += 3;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(10);
        pdf.text('Loan Terms:', margin, yPosition);
        yPosition += 5;

        pdf.setFont('times', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Interest Rate: ${this.formatPercent(carAInputs.interestRate)}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Term: ${carAInputs.financingYears} years (${carAInputs.financingYears * 12} months)`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Monthly Payment: ${this.formatCurrency(carAOutputs.finance.monthlyPayment)}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Total Interest: ${this.formatCurrency(carAOutputs.finance.totalInterest)}`, margin, yPosition);
        yPosition += 10;

        // Total Cost Summary
        yPosition = this.addSectionTitle(pdf, 'Total Cost Summary', yPosition);
        yPosition += 3;

        const financeSummaryRows: (string | number)[][] = [
          ['Total cost of ownership', carAOutputs.finance.totalCost],
          ['Cost per month', carAOutputs.finance.costPerMonth],
          [`Cost per ${carAInputs.region === 'Canada' ? 'kilometer' : 'mile'}`, this.formatCurrencyDetailed(carAOutputs.finance.costPerKm)]
        ];

        yPosition = this.addTable(pdf, ['Metric', 'Amount'], financeSummaryRows, yPosition, [106, 53]);

        this.addFooter(pdf, currentPage, totalPages, reportDate);
      }

      // ===========================================
      // PAGE 4: LEASE SCENARIO (if included)
      // ===========================================
      if (includeLease) {
        pdf.addPage();
        currentPage++;
        yPosition = this.addHeader(pdf, saveName || carAInputs.scenarioName || 'Vehicle Analysis');

        yPosition = this.addSectionTitle(pdf, 'LEASE SCENARIO - DETAILED ANALYSIS', yPosition);
        yPosition += 5;

        // Lease Details
        yPosition = this.addSectionTitle(pdf, 'Lease Details', yPosition);
        yPosition += 3;

        const leaseInitialRows: (string | number)[][] = [
          ['Vehicle price', carAInputs.totalCost],
          ['Sales tax', carAInputs.taxes],
          ['Fees and charges', carAInputs.fees],
          ['Lease down payment', carAInputs.leaseDownPayment],
          ['Capitalized cost', carAInputs.totalCost + carAInputs.taxes + carAInputs.fees - carAInputs.leaseDownPayment]
        ];

        yPosition = this.addTable(pdf, ['Description', 'Amount'], leaseInitialRows, yPosition, [106, 53]);

        // Lease Terms
        yPosition += 3;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(10);
        pdf.text('Lease Terms:', margin, yPosition);
        yPosition += 5;

        pdf.setFont('times', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Monthly Payment: ${this.formatCurrency(carAOutputs.lease.monthlyLeasePayment)}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Term: ${carAInputs.leaseTerm} years (${carAInputs.leaseTerm * 12} months)`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Mileage Allowance: ${carAInputs.totalMileageAllowance.toLocaleString()} ${carAInputs.region === 'Canada' ? 'km' : 'miles'}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Residual Value: ${this.formatCurrency(carAInputs.residualValue)}`, margin, yPosition);
        yPosition += 5;
        pdf.text(`Buyout Fees: ${this.formatCurrency(carAInputs.leaseFees)}`, margin, yPosition);
        yPosition += 10;

        // Total Cost Summary
        yPosition = this.addSectionTitle(pdf, 'Total Cost Summary', yPosition);
        yPosition += 3;

        const leaseSummaryRows: (string | number)[][] = [
          ['Total cost of ownership', carAOutputs.lease.totalCost],
          ['Cost per month', carAOutputs.lease.costPerMonth],
          [`Cost per ${carAInputs.region === 'Canada' ? 'kilometer' : 'mile'}`, this.formatCurrencyDetailed(carAOutputs.lease.costPerKm)]
        ];

        yPosition = this.addTable(pdf, ['Metric', 'Amount'], leaseSummaryRows, yPosition, [106, 53]);

        this.addFooter(pdf, currentPage, totalPages, reportDate);
      }

      // Save the PDF
      pdf.save(`car-analysis-${saveName || 'report'}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  }
}

export default CarPDFService;
